import {
    doc,
    getDoc,
    setDoc,
    updateDoc,
    collection,
    serverTimestamp,
    increment,
} from 'firebase/firestore';
import { db } from '../firebase';

// Plan Types
export type PlanType = 'starter' | 'home' | 'plus' | 'pro' | 'enterprise';
export type BillingCycle = 'monthly' | 'yearly';

// Cloud Function endpoint for Stripe Checkout
const CHECKOUT_FUNCTION_URL = 'https://createcheckoutsession-q72s33xxia-uc.a.run.app';
const CANCEL_FUNCTION_URL = 'https://us-central1-lotus-l1.cloudfunctions.net/cancelSubscription';

// Home plan uses Payment Links until price IDs are added to Cloud Function
const HOME_PAYMENT_LINKS = {
    monthly: 'https://buy.stripe.com/28EbJ0gRm2zb0ExarLeZ207',
    yearly: 'https://buy.stripe.com/eVqcN4fNi5Ln5YRbvPeZ206',
};

// Plan Configuration Interface
export interface PlanConfig {
    id: PlanType;
    name: string;
    tagline: string;
    price: {
        monthly: number;
        yearly: number;
    };
    limits: {
        aiCreditsPerMonth: number | null; // null = unlimited
        gradeWizardPagesPerMonth: number | null;
        digitalExamsPerMonth: number | null;
        examCreationsPerMonth: number | null;
        storageGB: number | null;
        interactiveGamesPerMonth?: number | null;
    };
    features: string[];
    popular?: boolean;
}

// User Subscription Interface
export interface UserSubscription {
    userId: string;
    planId: PlanType;
    billingCycle: BillingCycle;
    status: 'active' | 'cancelled' | 'past_due' | 'trialing';
    startDate: Date;
    endDate: Date;
    autoRenew: boolean;
    paymentMethod?: string;
    createdAt: Date;
    updatedAt: Date;
}

// Credit Usage Interface
export interface CreditUsage {
    userId: string;
    month: string; // YYYY-MM format
    aiCreditsUsed: number;
    gradeWizardPagesUsed: number;
    digitalExamsUsed: number;
    examCreationsUsed: number;
    storageUsedGB: number;
    interactiveGamesUsed?: number;
    lastUpdated: Date;
}

// Plan Configurations
export const PLAN_CONFIGS: Record<PlanType, PlanConfig> = {
    starter: {
        id: 'starter',
        name: 'Starter',
        tagline: 'The essential AI co-teacher for every classroom.',
        price: {
            monthly: 0,
            yearly: 0
        },
        limits: {
            aiCreditsPerMonth: 20,
            gradeWizardPagesPerMonth: 30,
            digitalExamsPerMonth: 1,
            examCreationsPerMonth: 10,
            storageGB: 0.5,
            interactiveGamesPerMonth: 2
        },
        features: [
            'AI Assistant (Spark) - 20 Credits/month',
            'GradeWizard - 30 pages/month',
            'Smart Exam Creator - 10 exams/month',
            'Printable PDF Exams',
            '1 Interactive Digital Exam/month',
            '2 AI Learning Games/month',
            'Library - 500MB storage'
        ]
    },
    home: {
        id: 'home',
        name: 'SparkSkool Home',
        tagline: 'For parents, tutors, and homeschoolers.',
        price: {
            monthly: 12,
            yearly: 120  // $10/mo when billed yearly
        },
        limits: {
            aiCreditsPerMonth: 200,
            gradeWizardPagesPerMonth: 350,
            digitalExamsPerMonth: 15,
            examCreationsPerMonth: 70,
            storageGB: 10,
            interactiveGamesPerMonth: 3
        },
        features: [
            'AI Assistant (Spark) - 200 Credits/month',
            'GradeWizard - 350 pages/month',
            'Smart Exam Creator - 70 exams/month',
            '15 Interactive Digital Exams/month',
            '3 AI Learning Games/month',
            'Library - 10GB storage',
            'Community & Knowledge Base support'
        ]
    },
    plus: {
        id: 'plus',
        name: 'Plus',
        tagline: 'The complete AI toolkit to automate your workflow and reclaim your time.',
        price: {
            monthly: 15,
            yearly: 144  // $12/mo when billed yearly
        },
        limits: {
            aiCreditsPerMonth: 300,
            gradeWizardPagesPerMonth: 500,
            digitalExamsPerMonth: 25,
            examCreationsPerMonth: 100,
            storageGB: 20,
            interactiveGamesPerMonth: 5
        },
        features: [
            'Everything in Starter, plus:',
            'AI Assistant (Spark) - 300 Credits/month',
            'GradeWizard - 500 pages/month',
            'Smart Exam Creator - 100 exams/month',
            '25 Interactive Digital Exams/month',
            '5 AI Learning Games/month',
            'Library - 20GB storage',
            'Advanced Analytics',
            'Priority Support'
        ],
        popular: true
    },
    pro: {
        id: 'pro',
        name: 'Pro',
        tagline: 'For the pioneers. Collaborate with your team and get early access to the future.',
        price: {
            monthly: 25,
            yearly: 250
        },
        limits: {
            aiCreditsPerMonth: null, // Unlimited
            gradeWizardPagesPerMonth: 1500,
            digitalExamsPerMonth: null, // Unlimited
            examCreationsPerMonth: 500,
            storageGB: 100,
            interactiveGamesPerMonth: 10
        },
        features: [
            'Everything in Plus, plus:',
            'AI Assistant (Spark) - Unlimited Credits',
            'GradeWizard - 1,500 pages/month',
            'Smart Exam Creator - 500 exams/month',
            'Library - 100GB storage',
            'Learning Games Converter - 10 Interactive Games',
            'Unlimited Printable PDF & Digital Exams',
            'Early Access to Experience Lab',
            'Live Chat Support'
        ]
    },
    enterprise: {
        id: 'enterprise',
        name: 'Enterprise',
        tagline: 'Custom solution for schools and districts.',
        price: {
            monthly: 0,
            yearly: 0
        },
        limits: {
            aiCreditsPerMonth: null,
            gradeWizardPagesPerMonth: null,
            digitalExamsPerMonth: null,
            examCreationsPerMonth: null,
            storageGB: null,
            interactiveGamesPerMonth: null
        },
        features: [
            'Everything in Pro, plus:',
            'Unlimited Usage - No caps on AI Tasks, grading pages, exam creations, or storage',
            'Full Admin Dashboard',
            'School-wide analytics, user management, and content controls',
            'LMS & SIS Integration',
            'Enterprise-Grade Security',
            'Single Sign-On (SSO)',
            'Dedicated Account Manager',
            'Professional Development Sessions'
        ]
    }
};

/**
 * Create a Stripe Checkout Session via Cloud Function.
 * This ensures subscription metadata (userId, planId, billingCycle) is set,
 * which the webhook handler needs to write to Firestore.
 * Home plan falls back to Payment Links until its price IDs are added.
 */
export const redirectToStripe = async (
    planId: 'home' | 'plus' | 'pro',
    billingCycle: BillingCycle,
    userId?: string,
    email?: string
): Promise<void> => {
    // Home plan: use Payment Links (no Cloud Function price IDs yet)
    if (planId === 'home') {
        const link = HOME_PAYMENT_LINKS[billingCycle];
        if (link) {
            const url = new URL(link);
            if (userId) url.searchParams.set('client_reference_id', userId);
            if (email) url.searchParams.set('prefilled_email', email);
            window.location.href = url.toString();
        }
        return;
    }

    // Plus & Pro: use createCheckoutSession Cloud Function
    if (!userId || !email) {
        console.error('userId and email are required for checkout');
        return;
    }

    try {
        const response = await fetch(CHECKOUT_FUNCTION_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                planId,
                billingCycle,
                userId,
                userEmail: email,
            }),
        });

        const data = await response.json();

        if (response.ok && data.url) {
            window.location.href = data.url;
        } else {
            console.error('Failed to create checkout session:', data.error);
            alert('Failed to start checkout. Please try again.');
        }
    } catch (error) {
        console.error('Error creating checkout session:', error);
        alert('An error occurred. Please try again.');
    }
};

/**
 * Cancel a subscription via Cloud Function (Stripe cancel at period end)
 */
export const cancelSubscriptionViaStripe = async (params: {
    userId: string;
    subscriptionId: string;
}): Promise<{ success: boolean; message?: string }> => {
    try {
        const response = await fetch(CANCEL_FUNCTION_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                userId: params.userId,
                subscriptionId: params.subscriptionId,
            }),
        });

        const data = await response.json();

        if (response.ok && data.success) {
            return { success: true, message: data.message };
        } else {
            return { success: false, message: data.error || 'Failed to cancel subscription' };
        }
    } catch (error) {
        console.error('Error canceling subscription:', error);
        return { success: false, message: 'An error occurred. Please try again.' };
    }
};

/**
 * Record a subscription cancellation request to Firestore
 */
export const requestCancelSubscription = async (params: {
    userId: string;
    email: string;
    planName?: string;
}): Promise<boolean> => {
    try {
        const { userId, email, planName } = params;
        const ref = doc(collection(db, 'cancellation_requests'));
        await setDoc(ref, {
            id: ref.id,
            userId,
            email,
            planName: planName || 'unknown',
            status: 'pending',
            createdAt: serverTimestamp()
        });
        return true;
    } catch (error) {
        console.error('Error recording cancellation request:', error);
        return false;
    }
};

// Get user's current subscription
export const getUserSubscription = async (userId: string): Promise<UserSubscription | null> => {
    try {
        const subscriptionRef = doc(db, 'user_subscriptions', userId);
        const subscriptionSnap = await getDoc(subscriptionRef);

        if (subscriptionSnap.exists()) {
            const data = subscriptionSnap.data();
            return {
                ...data,
                startDate: data.startDate.toDate(),
                endDate: data.endDate.toDate(),
                createdAt: data.createdAt.toDate(),
                updatedAt: data.updatedAt.toDate()
            } as UserSubscription;
        }

        // If no subscription exists, create a default starter plan
        const defaultSubscription: UserSubscription = {
            userId,
            planId: 'starter',
            billingCycle: 'monthly',
            status: 'active',
            startDate: new Date(),
            endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), // 1 year from now
            autoRenew: false,
            createdAt: new Date(),
            updatedAt: new Date()
        };

        await setDoc(subscriptionRef, {
            ...defaultSubscription,
            startDate: serverTimestamp(),
            endDate: serverTimestamp(),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });

        return defaultSubscription;
    } catch (error) {
        console.error('Error getting user subscription:', error);
        return null;
    }
};

// Get current month's usage
export const getCurrentMonthUsage = async (userId: string): Promise<CreditUsage | null> => {
    try {
        const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM format
        const usageRef = doc(db, 'user_usage', `${userId}_${currentMonth}`);
        const usageSnap = await getDoc(usageRef);

        if (usageSnap.exists()) {
            const data = usageSnap.data();
            return {
                userId: data.userId || userId,
                month: data.month || currentMonth,
                aiCreditsUsed: data.aiCreditsUsed || 0,
                gradeWizardPagesUsed: data.gradeWizardPagesUsed || 0,
                digitalExamsUsed: data.digitalExamsUsed || 0,
                examCreationsUsed: data.examCreationsUsed || 0,
                storageUsedGB: data.storageUsedGB || 0,
                interactiveGamesUsed: data.interactiveGamesUsed || 0,
                lastUpdated: data.lastUpdated ? data.lastUpdated.toDate() : new Date()
            } as CreditUsage;
        }

        // Create initial usage record for the month
        const initialUsage: CreditUsage = {
            userId,
            month: currentMonth,
            aiCreditsUsed: 0,
            gradeWizardPagesUsed: 0,
            digitalExamsUsed: 0,
            examCreationsUsed: 0,
            storageUsedGB: 0,
            interactiveGamesUsed: 0,
            lastUpdated: new Date()
        };

        await setDoc(usageRef, {
            ...initialUsage,
            lastUpdated: serverTimestamp()
        });

        return initialUsage;
    } catch (error) {
        console.error('Error getting current month usage:', error);
        return null;
    }
};

// Update usage (increment counters)
export const updateUsage = async (
    userId: string,
    updates: Partial<Pick<CreditUsage, 'aiCreditsUsed' | 'gradeWizardPagesUsed' | 'digitalExamsUsed' | 'examCreationsUsed' | 'storageUsedGB' | 'interactiveGamesUsed'>>
): Promise<boolean> => {
    try {
        const currentMonth = new Date().toISOString().slice(0, 7);
        const usageRef = doc(db, 'user_usage', `${userId}_${currentMonth}`);

        // Prepare increment updates
        const incrementUpdates: any = {
            lastUpdated: serverTimestamp()
        };

        Object.entries(updates).forEach(([key, value]) => {
            if (value && value > 0) {
                incrementUpdates[key] = increment(value);
            }
        });

        await updateDoc(usageRef, incrementUpdates);
        return true;
    } catch (error) {
        console.error('Error updating usage:', error);
        return false;
    }
};

// Check if user can perform an action (has enough credits/limits)
export const canPerformAction = async (
    userId: string,
    action: 'aiCredits' | 'gradeWizardPages' | 'digitalExams' | 'examCreations' | 'storage' | 'interactiveGames',
    amount: number = 1
): Promise<{ allowed: boolean; reason?: string }> => {
    try {
        const [subscription, usage] = await Promise.all([
            getUserSubscription(userId),
            getCurrentMonthUsage(userId)
        ]);

        if (!subscription || !usage) {
            return { allowed: false, reason: 'Unable to verify subscription or usage' };
        }

        const planLimits = PLAN_CONFIGS[subscription.planId].limits;

        switch (action) {
            case 'aiCredits':
                if (planLimits.aiCreditsPerMonth === null) return { allowed: true }; // Unlimited
                if (usage.aiCreditsUsed + amount > planLimits.aiCreditsPerMonth) {
                    return { allowed: false, reason: 'AI credits limit exceeded for this month' };
                }
                break;

            case 'gradeWizardPages':
                if (planLimits.gradeWizardPagesPerMonth === null) return { allowed: true }; // Unlimited
                if (usage.gradeWizardPagesUsed + amount > planLimits.gradeWizardPagesPerMonth) {
                    return { allowed: false, reason: 'GradeWizard pages limit exceeded for this month' };
                }
                break;

            case 'digitalExams':
                if (planLimits.digitalExamsPerMonth === null) return { allowed: true }; // Unlimited
                if (usage.digitalExamsUsed + amount > planLimits.digitalExamsPerMonth) {
                    return { allowed: false, reason: 'Digital exams limit exceeded for this month' };
                }
                break;

            case 'examCreations':
                if (planLimits.examCreationsPerMonth === null) return { allowed: true }; // Unlimited
                if ((usage.examCreationsUsed || 0) + amount > planLimits.examCreationsPerMonth) {
                    return { allowed: false, reason: 'Exam creations limit exceeded for this month' };
                }
                break;

            case 'storage':
                if (planLimits.storageGB === null) return { allowed: true }; // Unlimited
                if (usage.storageUsedGB + amount > planLimits.storageGB) {
                    return { allowed: false, reason: 'Storage limit exceeded' };
                }
                break;

            case 'interactiveGames':
                if (planLimits.interactiveGamesPerMonth === null) return { allowed: true }; // Unlimited
                if ((usage.interactiveGamesUsed || 0) + amount > (planLimits.interactiveGamesPerMonth || 0)) {
                    return { allowed: false, reason: 'Interactive games limit exceeded for this month' };
                }
                break;

            default:
                return { allowed: false, reason: 'Unknown action type' };
        }

        return { allowed: true };
    } catch (error) {
        console.error('Error checking action permission:', error);
        return { allowed: false, reason: 'Error checking permissions' };
    }
};

// Change user subscription
export const changeSubscription = async (
    userId: string,
    newPlanId: PlanType,
    billingCycle: BillingCycle
): Promise<boolean> => {
    try {
        const subscriptionRef = doc(db, 'user_subscriptions', userId);
        const currentSubscription = await getUserSubscription(userId);

        if (!currentSubscription) {
            return false;
        }

        const endDate = new Date();
        if (billingCycle === 'monthly') {
            endDate.setMonth(endDate.getMonth() + 1);
        } else {
            endDate.setFullYear(endDate.getFullYear() + 1);
        }

        await updateDoc(subscriptionRef, {
            planId: newPlanId,
            billingCycle,
            endDate: endDate,
            updatedAt: serverTimestamp()
        });

        return true;
    } catch (error) {
        console.error('Error changing subscription:', error);
        return false;
    }
};

// Record a billing inquiry to Firestore
export const recordBillingInquiry = async (params: {
    userId: string;
    email: string;
    subject: string;
    message: string;
}): Promise<boolean> => {
    try {
        const { userId, email, subject, message } = params;
        const ref = doc(collection(db, 'billing_inquiries'));
        await setDoc(ref, {
            id: ref.id,
            userId,
            email,
            subject,
            message,
            createdAt: serverTimestamp()
        });
        return true;
    } catch (error) {
        console.error('Error recording billing inquiry:', error);
        return false;
    }
};
